import { Stack, TextColors, Typography } from '@sureco/design-system'
import { Button, Modal } from '@surecompanies/core_components'
import RHFToggle from 'enrollment-platform/components/form/hookform/Toogle/RHFToogle'
import React from 'react'
import { useForm } from 'react-hook-form'
import RowSection from '../RowSection'

export type CustomPayrollConfigurationFormValues = {
  customReportUploadHris: boolean
  customReportHistoryDisplay: boolean
}

type CustomPayrollConfigurationFormProps = {
  customReportUploadHris?: boolean
  customReportHistoryDisplay?: boolean
  isCustomPayrollConfigurationEnabled: boolean
  isSaving: boolean
  onFormSubmit: (data: CustomPayrollConfigurationFormValues) => Promise<unknown>
}

export default function CustomPayrollConfigurationForm({
  customReportUploadHris,
  customReportHistoryDisplay,
  isCustomPayrollConfigurationEnabled,
  isSaving,
  onFormSubmit,
}: CustomPayrollConfigurationFormProps) {
  // An unset config means off, matching how the scheduled export reads it.
  const storedSettings = {
    customReportUploadHris: Boolean(customReportUploadHris),
    customReportHistoryDisplay: Boolean(customReportHistoryDisplay),
  }
  const { control, handleSubmit, watch } =
    useForm<CustomPayrollConfigurationFormValues>({
      defaultValues: storedSettings,
      // `values` keeps the toggles in sync with a background refetch, so Save never resends
      // the settings the form was first seeded with. `keepDirtyValues` stops that sync from
      // yanking a toggle the admin has already flipped — an unsaved edit survives a refetch.
      values: storedSettings,
      resetOptions: { keepDirtyValues: true },
    })

  const isUploadEnabled = watch('customReportUploadHris')
  const isHistoryDisplayEnabled = watch('customReportHistoryDisplay')

  if (!isCustomPayrollConfigurationEnabled) return null

  const handleSaveClick = () => {
    Modal.confirm({
      title: 'Save custom payroll configuration?',
      content:
        'The HRIS payroll upload is applied to every customer sharing this custom report, because they share one generated file. The history display applies to this customer only.',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: async () => {
        await handleSubmit(async (data) => {
          await onFormSubmit(data)
        })()
      },
    })
  }

  return (
    <RowSection title="Custom Payroll Configuration">
      <form>
        <Stack space={12}>
          <Typography size={14}>
            Post the custom payroll report to the HRIS payroll bucket for
            partner pickup (Like FlexSpring), in addition to the custom report
            bucket.
          </Typography>
          <Stack direction="row" align="center" space={10}>
            <RHFToggle name="customReportUploadHris" control={control} />
            <Typography size={16} color="#003146">
              Upload to HRIS Payroll{' '}
              <Typography
                color={isUploadEnabled ? TextColors.primary : TextColors.error}
                size={16}
                bold
              >
                {isUploadEnabled ? 'ON' : 'OFF'}
              </Typography>
            </Typography>
          </Stack>
          <Stack direction="row" align="center" space={10}>
            <RHFToggle name="customReportHistoryDisplay" control={control} />
            <Typography size={16} color="#003146">
              Show Custom Report History{' '}
              <Typography
                color={
                  isHistoryDisplayEnabled
                    ? TextColors.primary
                    : TextColors.error
                }
                size={16}
                bold
              >
                {isHistoryDisplayEnabled ? 'ON' : 'OFF'}
              </Typography>
            </Typography>
          </Stack>
          <Typography size={14}>
            Show the generated custom payroll reports on the customer&apos;s
            Company Profile payroll page, so they can download past files.
          </Typography>
          <Stack direction="row" justify="flex-start">
            <Button type="primary" onClick={handleSaveClick} loading={isSaving}>
              Save
            </Button>
          </Stack>
        </Stack>
      </form>
    </RowSection>
  )
}
